import React, { useState, useEffect } from "react";
import { Button, Form, FormGroup, Input, Label, Alert, Spinner } from "reactstrap";
import axios from "axios";
import Select, { createFilter } from "react-select";
import { API_URL_comision_multi } from "../../../constants";

import { getAuthHeaders, getCurrentUser } from "../../../utils/auth";
const ComisionVentaForm = ({
  proyectoCode,
  proyectoName,
  proyectoMoneda,
  estudiantesIngreso,
  partners,
  admin = null,
  resetState,
  toggle
}) => {
  const [formData, setFormData] = useState({
    pk: 0,
    pago_por: 'Comision de Venta',
    iva: 0,
    retencion: 0,
    isr: 0,
    subcantidad: 0,
    cantidad: 0,
    instructor: '',
    instructor_code: '',
    comision_multi_code: '',
    admin_del_curso: '',
    adminCostPercentage: '',
    adminCostAmount: '',
    costType: 'percentage', // 'percentage' or 'fixed'
    moneda: '',
    tipo_cambio: null,
    proyecto_code: '',
    proyecto: '',
    mod_user: ''
  });

  const [errorMessage, setErrorMessage] = useState("");
  const [validationErrors, setValidationErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    const username = getCurrentUser();

    console.log('[ComisionVentaForm] Props:', {
      estudiantesIngreso,
      proyectoCode,
      proyectoName,
      admin
    });

    // Set project data (moneda inherited from proyecto)
    setFormData(prev => ({
      ...prev,
      proyecto_code: proyectoCode,
      proyecto: proyectoName,
      moneda: proyectoMoneda || 'USD',
      mod_user: username
    }));

    // If editing, load admin data
    if (admin) {
      const {
        pk,
        instructor,
        moneda,
        tipo_cambio,
        proyecto,
        proyecto_code,
        comision_multi_code,
        cantidad,
        iva,
        isr,
        retencion,
        subcantidad
      } = admin;

      // ✅ FIXED: Safe calculation to avoid NaN
      let calculatedPercentage = '';
      let costType = 'percentage';

      if (estudiantesIngreso && estudiantesIngreso > 0 && subcantidad) {
        calculatedPercentage = (subcantidad / estudiantesIngreso * 100).toFixed(2);

        // Determine if this was saved as percentage or fixed amount
        // If the calculated percentage is a whole number or has few decimals, likely percentage
        // Otherwise, it was probably a fixed amount
        const roundedPercentage = Math.round(parseFloat(calculatedPercentage) * 100) / 100;
        const recalculatedAmount = estudiantesIngreso * roundedPercentage / 100;
        const difference = Math.abs(recalculatedAmount - subcantidad);

        // If difference is less than 0.01, it was probably saved as percentage
        if (difference > 0.01) {
          costType = 'fixed';
        }
      }

      console.log('[ComisionVentaForm] Edit mode calculation:', {
        subcantidad,
        estudiantesIngreso,
        calculatedPercentage,
        costType
      });

      setFormData({
        pk: pk || 0,
        instructor: instructor || '',
        admin_del_curso: instructor || '', // Use instructor as the vendedor
        moneda: moneda || proyectoMoneda || 'USD',
        tipo_cambio: tipo_cambio ?? null,
        proyecto: proyecto || proyectoName,
        proyecto_code: proyecto_code || proyectoCode,
        comision_multi_code: comision_multi_code || '',
        cantidad: cantidad || 0,
        iva: iva || 0,
        isr: isr || 0,
        retencion: retencion || 0,
        subcantidad: subcantidad || 0,
        adminCostPercentage: costType === 'percentage' ? calculatedPercentage : '',
        adminCostAmount: costType === 'fixed' ? subcantidad : '',
        costType: costType,
        pago_por: 'Comision de Venta',
        mod_user: username
      });
    }
  }, [proyectoCode, proyectoName, proyectoMoneda, admin, estudiantesIngreso]);

  const generateRandomString = (length) => {
    const characters = 'ABCDEFGHJKLMNOPQRSTUVWXYZ23456789';
    let random_string = '';
    for (let i = 0; i < length; i++) {
      random_string += characters.charAt(Math.floor(Math.random() * characters.length));
    }
    return random_string;
  };

  const validateForm = () => {
    const errors = {};
    const { admin_del_curso, adminCostPercentage, adminCostAmount, costType, moneda } = formData;

    if (!admin_del_curso || admin_del_curso.trim() === '') {
      errors.admin_del_curso = 'Por favor selecciona un vendedor';
    }

    if (costType === 'percentage') {
      if (!adminCostPercentage || adminCostPercentage === '') {
        errors.adminCostPercentage = 'Por favor ingresa el porcentaje';
      } else if (parseFloat(adminCostPercentage) < 0 || parseFloat(adminCostPercentage) > 100) {
        errors.adminCostPercentage = 'El porcentaje debe estar entre 0 y 100';
      }
    } else {
      if (!adminCostAmount || adminCostAmount === '') {
        errors.adminCostAmount = 'Por favor ingresa la cantidad';
      } else if (parseFloat(adminCostAmount) < 0) {
        errors.adminCostAmount = 'La cantidad debe ser mayor o igual a 0';
      }
    }

    // moneda validation removed — inherited from project

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const onChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Clear validation error for this field
    if (validationErrors[name]) {
      setValidationErrors(prev => ({
        ...prev,
        [name]: null
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate form
    if (!validateForm()) {
      setErrorMessage('Por favor completa todos los campos obligatorios marcados con *');
      setTimeout(() => {
        const firstError = document.querySelector('.is-invalid-custom');
        if (firstError) {
          firstError.scrollIntoView({ behavior: 'smooth', block: 'center' });
          const input = firstError.querySelector('input, select');
          if (input) input.focus();
        }
      }, 100);
      return;
    }

    setIsSubmitting(true);
    setErrorMessage('');

    const headers = getAuthHeaders();

    try {
      // Find selected partner to get additional data
      const selectedPartner = partners.find(p => p.partner === formData.admin_del_curso);

      let partnerData = {};
      if (selectedPartner) {
        partnerData = {
          iva: selectedPartner.iva || 0,
          isr: selectedPartner.isr || 0,
          retencion: selectedPartner.retencion || 0,
          instructor_code: selectedPartner.partner_code || '',
          instructor: selectedPartner.partner || ''
        };
      }

      // Calculate subcantidad and cantidad based on costType
      let subTotal;
      if (formData.costType === 'percentage') {
        subTotal = estudiantesIngreso * parseFloat(formData.adminCostPercentage) / 100;
      } else {
        subTotal = parseFloat(formData.adminCostAmount);
      }

      console.log('[ComisionVentaForm] Saving with values:', {
        estudiantesIngreso,
        costType: formData.costType,
        adminCostPercentage: formData.adminCostPercentage,
        adminCostAmount: formData.adminCostAmount,
        subTotal
      });

      // Prepare data
      const submitData = {
        ...partnerData,
        moneda: formData.moneda,
        tipo_cambio: formData.tipo_cambio,
        proyecto: formData.proyecto,
        proyecto_code: formData.proyecto_code,
        comision_multi_code: admin ? formData.comision_multi_code : generateRandomString(5),
        cantidad: subTotal,
        subcantidad: subTotal,
        pago_por: formData.pago_por,
        mod_user: formData.mod_user
      };

      console.log('[ComisionVentaForm] Submit data:', submitData);

      // Create or update
      if (admin) {
        // ✅ FIXED: Added trailing slash
        const url = `${API_URL_comision_multi}${formData.pk}/`;
        await axios.put(url, submitData, { headers });
      } else {
        await axios.post(API_URL_comision_multi, submitData, { headers });
      }

      resetState();
      toggle();
    } catch (error) {
      console.error("Error saving Comision de Venta:", error);

      // Handle server-side validation errors
      if (error.response?.data) {
        const serverErrors = error.response.data;
        if (typeof serverErrors === 'object' && !Array.isArray(serverErrors)) {
          setValidationErrors(serverErrors);
          setErrorMessage('Error de validación. Por favor revisa los campos.');
        } else {
          setErrorMessage('Error al guardar. Por favor verifica los datos.');
        }
      } else {
        setErrorMessage('Error de conexión. Por favor intenta nuevamente.');
      }
      setIsSubmitting(false);
    }
  };

  const h3Style = { color: 'blue' };

  // ✅ FIXED: Safe calculation to avoid NaN in display
  const ingresoValue = parseFloat(estudiantesIngreso) || 0;
  let calculatedComision;

  if (formData.costType === 'percentage') {
    const percentageValue = parseFloat(formData.adminCostPercentage) || 0;
    calculatedComision = ingresoValue * percentageValue / 100;
  } else {
    calculatedComision = parseFloat(formData.adminCostAmount) || 0;
  }

  console.log('[ComisionVentaForm] Display calculation:', {
    ingresoValue,
    costType: formData.costType,
    adminCostPercentage: formData.adminCostPercentage,
    adminCostAmount: formData.adminCostAmount,
    calculatedComision
  });

  // Filter option for react-select (search without accents)
  const filterOption = createFilter({
    ignoreAccents: true,
    ignoreCase: true,
    trim: true,
    matchFrom: "any",
  });

  // Options for Select components
  const partnerOptions = Array.isArray(partners)
    ? partners.map(partner => ({ value: partner.partner, label: partner.partner }))
    : [];

  const fxRowCurrency = formData.moneda || 'USD';
  const fxProjectCurrency = proyectoMoneda || 'USD';
  const fxCurrency = fxRowCurrency !== 'USD' ? fxRowCurrency : fxProjectCurrency;
  const showTipoCambio = fxRowCurrency !== 'USD' || fxProjectCurrency !== 'USD';

  return (
    <div>
      <h3>{proyectoName}</h3>
      {proyectoCode && <h3 style={h3Style}>{proyectoCode.toUpperCase()}</h3>}

      {/* ✅ Display Ingresos prominently */}
      <h5 style={{ marginBottom: '20px' }}>
        <strong>Ingresos:</strong>
      </h5>
      <h4 style={{ color: '#28a745', marginBottom: '20px' }}>
        {ingresoValue.toLocaleString(undefined, {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2
        })}
      </h4>

      <div className="form-requirements-note">
        <strong>Nota:</strong> Los campos marcados con <span className="required-asterisk">*</span> son obligatorios.
      </div>

      <Form onSubmit={handleSubmit}>
        <FormGroup>
          <Label for="admin_del_curso">
            Vendedor<span className="required-asterisk">*</span>
          </Label>
          <Select
            options={partnerOptions}
            value={partnerOptions.find((o) => o.value === formData.admin_del_curso) || null}
            onChange={(opt) => {
              setFormData(prev => ({ ...prev, admin_del_curso: opt ? opt.value : "" }));
              setValidationErrors(prev => ({ ...prev, admin_del_curso: '' }));
            }}
            placeholder="Selecciona vendedor"
            isClearable
            filterOption={filterOption}
            className={validationErrors.admin_del_curso ? 'is-invalid' : ''}
          />
          {validationErrors.admin_del_curso && (
            <div className="invalid-feedback-custom">{validationErrors.admin_del_curso}</div>
          )}
        </FormGroup>

        <FormGroup>
          <Label for="costType">
            Tipo de Costo<span className="required-asterisk">*</span>
          </Label>
          <Input
            type="select"
            name="costType"
            value={formData.costType}
            onChange={(e) => {
              setFormData(prev => ({
                ...prev,
                costType: e.target.value,
                // Clear the other field when switching
                adminCostPercentage: e.target.value === 'percentage' ? prev.adminCostPercentage : '',
                adminCostAmount: e.target.value === 'fixed' ? prev.adminCostAmount : ''
              }));
              setValidationErrors({});
            }}
          >
            <option value="percentage">Porcentaje (%)</option>
            <option value="fixed">Cantidad Fija</option>
          </Input>
        </FormGroup>

        {formData.costType === 'percentage' ? (
          <FormGroup>
            <Label for="adminCostPercentage">
              Porcentaje<span className="required-asterisk">*</span>
            </Label>
            <Input
              type="number"
              name="adminCostPercentage"
              onChange={onChange}
              value={formData.adminCostPercentage}
              className={validationErrors.adminCostPercentage ? 'is-invalid-custom' : ''}
              step="0.01"
              placeholder="0.00"
            />
            {validationErrors.adminCostPercentage && (
              <div className="invalid-feedback-custom">{validationErrors.adminCostPercentage}</div>
            )}
            <small className="form-text text-muted">
              Se calculará: Ingreso del proyecto × {formData.adminCostPercentage || 0}% = {calculatedComision.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </small>
          </FormGroup>
        ) : (
          <FormGroup>
            <Label for="adminCostAmount">
              Cantidad Fija<span className="required-asterisk">*</span>
            </Label>
            <Input
              type="number"
              name="adminCostAmount"
              onChange={onChange}
              value={formData.adminCostAmount}
              className={validationErrors.adminCostAmount ? 'is-invalid-custom' : ''}
              step="0.01"
              placeholder="0.00"
            />
            {validationErrors.adminCostAmount && (
              <div className="invalid-feedback-custom">{validationErrors.adminCostAmount}</div>
            )}
            <small className="form-text text-muted">
              Ingresa la cantidad específica de comisión
            </small>
          </FormGroup>
        )}

        <FormGroup>
          <Label for="moneda">Moneda (desde proyecto):</Label>
          <Input
            type="text"
            name="moneda"
            value={formData.moneda || 'USD'}
            disabled
            style={{ backgroundColor: '#f0f0f0', cursor: 'not-allowed' }}
          />
          <small className="form-text text-muted">
            La moneda se hereda del proyecto y no se puede cambiar aquí
          </small>
        </FormGroup>

        {showTipoCambio && (
          <FormGroup>
            <Label for="tipo_cambio">
              Tipo de Cambio ({fxCurrency} por 1 USD):
            </Label>
            <Input
              type="number"
              step="0.0001"
              min="0"
              name="tipo_cambio"
              placeholder="ej. 17 (vacío para usar la tasa mensual)"
              onChange={(e) => setFormData(prev => ({
                ...prev,
                tipo_cambio: e.target.value === "" ? null : Number(e.target.value),
              }))}
              value={formData.tipo_cambio ?? ""}
            />
            <small className="form-text text-muted">
              {fxRowCurrency !== 'USD'
                ? 'Opcional. Si lo capturas, esta partida se convierte a USD dividiendo entre este número. Vacío = tasa del mes de inicio del proyecto.'
                : `Opcional. Si lo capturas, esta partida en USD se convierte a ${fxProjectCurrency} multiplicando por este número. Vacío = tasa del mes de inicio del proyecto.`}
            </small>
          </FormGroup>
        )}

        {/* ✅ Display calculated Comision prominently like in the image */}
        <FormGroup>
          <Label for="comision">Comision:</Label>
          <h4 style={{ color: '#007bff', fontWeight: 'bold' }}>
            {calculatedComision.toLocaleString(undefined, {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2
            })}
          </h4>
        </FormGroup>

        {errorMessage && <Alert color="danger">{errorMessage}</Alert>}

        <Button color="primary" disabled={isSubmitting}>
          {isSubmitting ? (
            <>
              <Spinner size="sm" className="me-2" /> Guardando...
            </>
          ) : (
            'Guardar'
          )}
        </Button>
      </Form>
    </div>
  );
};

export default ComisionVentaForm;
